import { z } from 'zod';

import {
  SPEECH_SUPER_DEFAULTS,
  SpeechSuperCommandEnum,
  SpeechSuperCoreTypeEnum,
  SpeechSuperDictTypeEnum,
  SpeechSuperDictDialectEnum,
  SpeechSuperAgeGroupEnum,
  SpeechSuperBooleanEnum,
} from '../constants/speechsuper.constants.ts';

// Connection Schemas
export const SpeechSuperSdkParamsSchema = z.object({
  version: z.number(),
  source: z.number(),
  protocol: z.number(),
});

export const SpeechSuperConnectAppParamsSchema = z.object({
  applicationId: z.string(),
  timestamp: z.string(),
  sig: z.string(),
});

export const SpeechSuperConnectSchema = z.object({
  cmd: z.literal(SpeechSuperCommandEnum.CONNECT),
  param: z.object({
    sdk: SpeechSuperSdkParamsSchema,
    app: SpeechSuperConnectAppParamsSchema,
  }),
});

// Base Assessment Schema with common parameters
export const SpeechSuperBaseRequestParamsSchema = z.object({
  tokenId: z.string(),
  refText: z.string(),
  getParam: z.number().default(SPEECH_SUPER_DEFAULTS.GET_PARAM).optional(),
  scale: z.number().default(SPEECH_SUPER_DEFAULTS.SCALE).optional(),
  precision: z.number().default(SPEECH_SUPER_DEFAULTS.PRECISION).optional(),
  agegroup: z.nativeEnum(SpeechSuperAgeGroupEnum).default(SPEECH_SUPER_DEFAULTS.AGE_GROUP).optional(),
  accent_dialect: z.string().optional(),
});

export const SpeechSuperWordRequestParamsSchema = SpeechSuperBaseRequestParamsSchema.extend({
  coreType: z.literal(SpeechSuperCoreTypeEnum.WORD),
  dict_type: z.nativeEnum(SpeechSuperDictTypeEnum).default(SPEECH_SUPER_DEFAULTS.DICT_TYPE).optional(),
  phoneme_output: z.number().default(SPEECH_SUPER_DEFAULTS.PHONEME_OUTPUT).optional(),
  dict_dialect: z.nativeEnum(SpeechSuperDictDialectEnum).optional(),
  customized_pron: z
    .object({
      type: z.string(),
      pron: z.record(z.array(z.string())),
    })
    .optional(),
  slack: z.number().min(-1).max(1).default(SPEECH_SUPER_DEFAULTS.SLACK).optional(),
});

export const SpeechSuperSentenceRequestParamsSchema = SpeechSuperBaseRequestParamsSchema.extend({
  coreType: z.literal(SpeechSuperCoreTypeEnum.SENTENCE),
  realtime_feedback: z.nativeEnum(SpeechSuperBooleanEnum).transform(Number).optional(),
  phoneme_output: z.nativeEnum(SpeechSuperBooleanEnum).transform(Number).optional(),
});

export const SpeechSuperAudioParamsSchema = z.object({
  audioType: z.string(),
  channel: z.number(),
  sampleBytes: z.number(),
  sampleRate: z.number(),
});

export const SpeechSuperStartAppParamsSchema = SpeechSuperConnectAppParamsSchema.extend({
  userId: z.string(),
});

export const SpeechSuperRequestParamsSchema = z.discriminatedUnion('coreType', [
  SpeechSuperWordRequestParamsSchema,
  SpeechSuperSentenceRequestParamsSchema,
]);

export const SpeechSuperAssessmentStartSchema = z.object({
  cmd: z.literal(SpeechSuperCommandEnum.START),
  param: z.object({
    app: SpeechSuperStartAppParamsSchema,
    audio: SpeechSuperAudioParamsSchema,
    request: SpeechSuperRequestParamsSchema,
  }),
});

export const SpeechSuperStopSchema = z.object({
  cmd: z.literal(SpeechSuperCommandEnum.STOP),
});

// Utility Types
export const SpeechSuperRequestSchema = z.discriminatedUnion('cmd', [
  SpeechSuperConnectSchema,
  SpeechSuperAssessmentStartSchema,
  SpeechSuperStopSchema,
]);

export type SpeechSuperSdkParams = z.infer<typeof SpeechSuperSdkParamsSchema>;
export type SpeechSuperConnectAppParams = z.infer<typeof SpeechSuperConnectAppParamsSchema>;
export type SpeechSuperStartAppParams = z.infer<typeof SpeechSuperStartAppParamsSchema>;
export type SpeechSuperConnect = z.infer<typeof SpeechSuperConnectSchema>;
export type SpeechSuperAudioParams = z.infer<typeof SpeechSuperAudioParamsSchema>;
export type SpeechSuperBaseRequestParams = z.infer<typeof SpeechSuperBaseRequestParamsSchema>;
export type SpeechSuperWordRequestParams = z.infer<typeof SpeechSuperWordRequestParamsSchema>;
export type SpeechSuperSentenceRequestParams = z.infer<typeof SpeechSuperSentenceRequestParamsSchema>;
export type SpeechSuperAssessmentStartParams = z.infer<typeof SpeechSuperAssessmentStartSchema>;
export type SpeechSuperStopParams = z.infer<typeof SpeechSuperStopSchema>;
export type SpeechSuperRequestParams = z.infer<typeof SpeechSuperRequestParamsSchema>;
export type SpeechSuperAudioMessage = ArrayBuffer;
export type SpeechSuperRequest = z.infer<typeof SpeechSuperRequestSchema>;
